import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Button, Link, Alert, Stack } from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { palette, FONT_DISPLAY, FONT_MONO } from '../theme';
import BrandMark from '../components/BrandMark';

const Login = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verifyPrompt, setVerifyPrompt] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError(''); setVerifyPrompt(false);
    try { await login(form.email, form.password); }
    catch (err: any) {
      const msg = err.response?.data?.error || 'Login failed';
      setError(msg);
      if (msg.toLowerCase().includes('verify your email')) setVerifyPrompt(true);
    } finally { setLoading(false); }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      bgcolor: palette.bg,
    }}>
      {/* Left: form */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 3, sm: 6 } }}>
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          <Stack direction="row" gap={1.5} alignItems="center" sx={{ mb: 5 }}>
            <BrandMark size={36} />
            <Box>
              <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 16, lineHeight: 1.1 }}>Workflow.Estate</Typography>
              <Typography sx={{ fontFamily: FONT_MONO, fontSize: 10.5, color: palette.inkMuted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Real-estate workflow</Typography>
            </Box>
          </Stack>

          <Typography component="h1" sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 32, letterSpacing: '-0.02em', mb: 1 }}>
            Sign in.
          </Typography>
          <Typography sx={{ color: palette.inkMuted, mb: 4 }}>
            Welcome back. Enter your work email to continue.
          </Typography>

          {error && (
            <Alert severity={verifyPrompt ? 'warning' : 'error'} sx={{ mb: 2.5, borderRadius: 1.5 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField margin="dense" required fullWidth id="email" label="Email Address" name="email"
              autoComplete="email" autoFocus value={form.email} onChange={handleChange} />
            <TextField margin="dense" required fullWidth name="password" label="Password" type="password"
              id="password" autoComplete="current-password" value={form.password} onChange={handleChange} />
            <Button type="submit" fullWidth variant="contained" disableElevation
              sx={{ mt: 3, mb: 2, height: 44, fontWeight: 600 }} disabled={loading}>
              {loading ? 'Signing in…' : 'Sign In'}
            </Button>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Link component={RouterLink} to="/register" variant="body2" underline="hover" sx={{ color: palette.accent, fontWeight: 600 }}>
                Create account
              </Link>
              <Link component={RouterLink} to="/individual/login" variant="body2" underline="hover" sx={{ color: palette.inkMuted }}>
                Solo agent →
              </Link>
            </Stack>

            {verifyPrompt && (
              <Button href="/verify-email" variant="outlined" sx={{ mt: 2 }} fullWidth>
                Resend Verification Email
              </Button>
            )}
          </Box>

          <Typography sx={{ mt: 6, fontFamily: FONT_MONO, fontSize: 10.5, color: palette.inkSubtle, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            © Workflow.Estate · Australia
          </Typography>
        </Box>
      </Box>

      {/* Right: brand panel (hidden on mobile) */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' }, alignItems: 'flex-end',
        background: `linear-gradient(160deg, ${palette.accent} 0%, ${palette.accentDark} 100%)`,
        color: '#fff', p: 6, position: 'relative', overflow: 'hidden',
      }}>
        {/* Subtle grain layer */}
        <Box aria-hidden sx={{
          position: 'absolute', inset: 0, opacity: 0.08,
          backgroundImage: 'repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 12px)',
        }} />
        <Box sx={{ position: 'relative', maxWidth: 520 }}>
          <Typography sx={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', opacity: 0.8, mb: 2 }}>
            / Calm. Continuous. Closed.
          </Typography>
          <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: { md: 44, lg: 56 }, letterSpacing: '-0.025em', lineHeight: 1.05, mb: 3 }}>
            From appraisal<br />to settlement —<br />in one workspace.
          </Typography>
          <Typography sx={{ opacity: 0.85, fontSize: 15, lineHeight: 1.6, maxWidth: 420 }}>
            Properties, open homes, contractors and tasks — every step defined, assigned and tracked.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
